import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';
import { DataService } from 'src/app/services/data.service';
import { CurrentUserService } from 'src/app/services/current-user.service';
import { SubscriptionStatus } from '../../../models/subscription-usage.model';
import { forkJoin } from 'rxjs';
import { DowngradeFeedbackComponent } from '../downgrade-feedback/downgrade-feedback.component';
import { InnerCommunicationDataPassService } from '../../services/inner-communication-data-pass.service';
import { DowngradeStatusResponse } from '../../models/downgrade-response.model';

@Component({
    selector: 'app-character-delete-confirmation',
    templateUrl: './character-delete-confirmation.component.html',
    styleUrls: ['./character-delete-confirmation.component.scss'],
    standalone: false
})
export class CharacterDeleteConfirmationComponent implements OnInit {

  upgradePlan: any;
  currentPlan: any;
  planUsage: SubscriptionStatus;
  userId: any;
  characterList: any[] = [];
  isLoading = false;
  isDeleting = false;
  deletedMapIds: any[] = [];
  autoDeletedCharacters: any[] = [];
  manuallyDeletedGameIds: any[] = [];
  manualCharactersToDelete: number = 0;
  downgradeResponse!: DowngradeStatusResponse;

  constructor(
    private activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private router: Router,
    private dataService: DataService,
    private currentUserService: CurrentUserService,
    private innerCommunicationDataPassService: InnerCommunicationDataPassService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.setupCurrentUserPlanUsage();
    this.getCharacterList();
  }

  setupCurrentUserPlanUsage() {
    this.currentUserService.planUsageData.subscribe((data: SubscriptionStatus) => {
      if (data) {
        this.planUsage = data;
        this.cdr.detectChanges();
      }
    });
  }

  manuallyDeletedCharacterIds: any[] = [];
  retiredCharIds: any[] = [];

  getCharacterList() {
    this.dataService.Getcharacterdetail(this.userId).subscribe((res: { character_details: any[] }) => {
      if (res.character_details && res.character_details?.length > 0) {
        this.characterList = res.character_details.filter((c: any) =>
          !this.manuallyDeletedCharacterIds.some(mdc => mdc.id === c.id) &&
          !this.retiredCharIds.some(rc => rc.id === c.id) &&
          !c.is_retired && !c.is_dead
        );
        this.calculateCharactersToDelete();
      } else {
        this.characterList = [];
        this.calculateCharactersToDelete();
      }
      this.cdr.detectChanges();
    });
  }

  calculateCharactersToDelete() {
    this.autoDeletedCharacters = this.characterList.filter(c => this.deletedMapIds.includes(c.map_code));
    const totalChars = this.characterList.length;
    const maxChars = this.downgradeResponse?.limits_comparison?.characters?.new_plan_limit ?? (this.upgradePlan?.features?.max_active_characters || 0);

    if (maxChars === -1) {
      this.manualCharactersToDelete = 0;
    } else {
      const remainingAfterAuto = totalChars - this.autoDeletedCharacters.length;
      this.manualCharactersToDelete = Math.max(0, remainingAfterAuto - maxChars);
    }
  }

  isCharacterAutoDeleted(char: any): boolean {
    return this.deletedMapIds.includes(char.map_code);
  }

  close() {
    this.activeModal.close('close');
    this.innerCommunicationDataPassService.childModalCloseEvent(true);
  }

  deleteSingleCharacter(char: any) {
    if (confirm('Are you sure you want to mark this character for deletion?')) {
      const charGameId = char.game_details ? char.game_details.game_id : null;
      this.manuallyDeletedCharacterIds.push({ id: char.id, game_id: charGameId });
      this.characterList = this.characterList.filter((c: any) => c.id !== char.id);
      this.calculateCharactersToDelete();
    }
  }

  get remainingRetiredSlots(): number {
    const retiredLimits = this.downgradeResponse?.limits_comparison?.retired_npcs;
    if (!retiredLimits) return -1;
    if (retiredLimits.new_plan_limit === -1) return -1;
    const maxAllowed = Math.max(0, retiredLimits.new_plan_limit - retiredLimits.current_usage);
    return Math.max(0, maxAllowed - this.retiredCharIds.length);
  }

  retireSingleCharacter(char: any) {
    const remaining = this.remainingRetiredSlots;
    if (remaining !== -1 && remaining <= 0) {
      if (confirm('You have reached the maximum allowed retired characters/NPCs for your new plan. To retire this character, please upgrade your plan. Would you like to subscribe?')) {
        this.subscribe();
      }
      return;
    }

    if (confirm('Are you sure you want to retire this character instead of deleting?')) {
      const charGameId = char.game_details ? char.game_details.game_id : null;
      this.retiredCharIds.push({ id: char.id, game_id: charGameId });
      this.characterList = this.characterList.filter((c: any) => c.id !== char.id);
      this.calculateCharactersToDelete();
    }
  }

  deleteAll() {
    this.isDeleting = true;
    const deleteObservables = this.characterList.map(char =>
      this.dataService.deleteCharacter(char.id, char.game_id)
    );

    if (deleteObservables.length > 0) {
      forkJoin(deleteObservables).subscribe({
        next: () => {
          this.isDeleting = false;
          this.getCharacterList();
        
          this.cdr.detectChanges();},
        error: (err: any) => {
          console.error('Error deleting characters', err);
          this.isDeleting = false;
        
          this.cdr.detectChanges();}
      });
    }
  }

  deleteCharacters() {
    this.activeModal.close('character-delete');
    this.router.navigate(['char-portfolio']);
  }

  next() {
    this.activeModal.close('next');
    const modalRef = this.modalService.open(DowngradeFeedbackComponent, {
      size: 'lg',
      backdrop: 'static',
      centered: true,
      keyboard: false
    });
    modalRef.componentInstance.upgradePlan = this.upgradePlan;
    modalRef.componentInstance.currentPlan = this.currentPlan;
    modalRef.componentInstance.userId = this.userId;
    modalRef.componentInstance.deletedMapIds = this.deletedMapIds;
    modalRef.componentInstance.manuallyDeletedGameIds = this.manuallyDeletedGameIds;
    modalRef.componentInstance.manuallyDeletedCharacterIds = this.manuallyDeletedCharacterIds;
    modalRef.componentInstance.retiredCharIds = this.retiredCharIds;
    modalRef.componentInstance.downgradeResponse = this.downgradeResponse;

    modalRef.result.then((result) => {
      if (result === 'next') {
        this.activeModal.close('next');
      } else if (result === 'close') {
        this.activeModal.close('close');
      }
    
          this.cdr.detectChanges();});
  }

  subscribe() {
    this.activeModal.close('subscribe');
    this.router.navigate(['/subscription']);
  }
}
